import {
    mediaBucketsEnabled,
    payloadMediaGridLayout,
    RESERVED_URL_CARD_HEIGHT,
    RESERVED_DIMLESS_MEDIA_HEIGHT,
    RESERVED_YOUTUBE_EMBED_HEIGHT,
    RESERVED_AUDIO_CARD_HEIGHT,
    mobileReplyMediaEstimatedWidth,
    payloadMediaContentWidth,
    payloadMediaGridTileWidth,
} from './mediaBuckets'
import { estimateTweetCardProjectionHeight, TWEET_SKELETON_HEIGHT_PX } from './tweetCardHeight'
import { resolveHeightEstimationSource } from './answerHeightFingerprint'
import {
    resolveAnswerGeometryProjection,
    resolveEmbeddedAnswerGeometryProjection,
} from './answerGeometryProjection'
import { extractOrdinalOutpointFromUrl } from 'components/shared/ordinalUrlUtils'
import {
    ANSWER_CONTENT_TOP_PADDING_PX,
    REPLY_CARD_BODYLESS_HEADER_EXTRA_HEIGHT_PX,
} from './staticGeometryChrome'
import { payloadMediaRowKind } from 'components/answers/Answer/payloadMediaKind'
import { projectionCombinesMedia } from './combinedMedia'
import { payloadMediaCarouselHeight, resolvePayloadMediaLayout } from './payloadMediaLayout'

export const STREAM_TITLE_FALLBACK_HEIGHT = 112
export const STREAM_REPLY_FALLBACK_HEIGHT = 78
export const STREAM_TEXT_LINE_HEIGHT = 20
export const STREAM_MEDIA_PLACEHOLDER_HEIGHT = 160
export const STREAM_URL_PLACEHOLDER_HEIGHT = 110
export const STREAM_FILE_ROW_HEIGHT = 34
export const STREAM_THIN_ANSWER_HEIGHT = 44

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const hasArrayItems = value => Array.isArray(value) && value.length > 0

const answerHasMedia = projection => !!projection?.media?.hasAnswerMedia

const answerHasPreview = projection => !!projection?.hasPreview

const answerHasRecording = projection => !!projection?.media?.hasRecording

const answerHasAudioPayload = projection =>
    Number(projection?.media?.aiAudioOutputCount || 0) > 0 ||
    (projection?.fileRows || []).some(file => file.audio)

const estimateTextLines = projection => {
    const explicitLineCount = Number(projection?.explicitLineCount || 0)
    if (explicitLineCount > 0) return explicitLineCount

    return Math.ceil(String(projection?.text || '').length / 72)
}

// Flag-gated constants calibrated against live rendered rows (see the estimate-vs-rendered
// harness): baseline row chrome, the real collapsed-text line clamp, grid gap, per-section
// chrome, and the UrlAnswer wrapper around link cards.
const BUCKETS_REPLY_CHROME_WITHOUT_CONTENT_TOP_PADDING_PX = 145
// Minimal 1-line stream title: header row 40 + text line 20 + vote controls row 48 + content
// paddings ~26, +11 safety (fonts/zoom render a few px taller on some platforms; under-reserve
// clips inside the card while over-reserve is a few px of internal padding). Recalibrated
// 2026-07 against rendered cards — the old 199 (pre-clipping calibration, when over-reserve
// was invisible) left a constant ~55px blank band inside every pinned title card.
const BUCKETS_TITLE_CHROME_WITHOUT_CONTENT_TOP_PADDING_PX = 130
export const STATIC_GEOMETRY_REPLY_BASE_HEIGHT_PX =
    BUCKETS_REPLY_CHROME_WITHOUT_CONTENT_TOP_PADDING_PX + ANSWER_CONTENT_TOP_PADDING_PX
export const STATIC_GEOMETRY_TITLE_BASE_HEIGHT_PX =
    BUCKETS_TITLE_CHROME_WITHOUT_CONTENT_TOP_PADDING_PX + ANSWER_CONTENT_TOP_PADDING_PX
const BUCKETS_MAX_TEXT_LINES = 16
const BUCKETS_WRAP_CHARS_PER_LINE = 72
const BUCKETS_UNBROKEN_TOKEN_CHARS_PER_LINE = 34
const BUCKETS_MIN_WRAP_CHARS_PER_LINE = 34
const BUCKETS_TEXT_HORIZONTAL_CHROME = 96
const BUCKETS_AVERAGE_CHAR_WIDTH_PX = 7.25
// Expanded text rows are frozen before the browser can measure their final wrapping. The
// average-width estimate is intentionally tight for collapsed rows to avoid bottom padding, but
// full long-form prose wraps several lines taller in StaticAnswerBody at stream-card widths.
const BUCKETS_EXPANDED_TEXT_WRAP_SAFETY_FACTOR = 1.25
const BUCKETS_SINGLE_IMAGE_CHROME = 16
const BUCKETS_MEDIA_SECTION_CHROME = 40
// Multi-item grids render bare (no outer frame, zero section insets — see
// payload-container.module.scss .mediaSectionBareGrid): the 40px framed-section chrome
// minus the 8px top + 8px bottom insets the bare grid no longer has.
const BUCKETS_MEDIA_GRID_CHROME = 24
const BUCKETS_MEDIA_GRID_GAP = 6
const BUCKETS_MIXED_MEDIA_ROW_GAP = 14
// AI-input provenance strip (AiInputMediaStrip): 12px top margin + 56px thumb row, fixed
// height regardless of item count (single row, horizontal scroll). ⚠️ Mirrors the
// .aiInputMediaStrip geometry in answer.module.scss — change together.
const BUCKETS_AI_INPUT_STRIP_HEIGHT = 68
const BUCKETS_URL_ANSWER_TOP_MARGIN = 15
// Ordinal link cards include the host footer inside the fixed preview plus one extra rendered
// line of answer-frame chrome that generic OpenGraph cards do not consume.
const BUCKETS_ORDINAL_URL_FRAME_CHROME = 20
const BUCKETS_URL_SELECTION_LINE_HEIGHT = 24
const BUCKETS_URL_SELECTION_MAX_LINES = 6
const BUCKETS_YOUTUBE_CHROME = 50
const BUCKETS_FILE_ROW_HEIGHT = 118
// The audio surface is the artwork card pinned to RESERVED_AUDIO_CARD_HEIGHT, inside the
// 1px-bordered payload surface (+2). The remaining 10px is the calibrated answer/payload wrapper
// contribution. The card and PayloadContainer read the same constant, so estimate === rendered.
const BUCKETS_AUDIO_PAYLOAD_SURFACE_OUTER_HEIGHT = RESERVED_AUDIO_CARD_HEIGHT + 2
const BUCKETS_AUDIO_PAYLOAD_CHROME = 10
// A generated payload carries its model/metadata attribution below the media frame. The live
// compact audio row measures 15.34px taller with that line, so round up to avoid clipping.
const BUCKETS_AUDIO_ATTRIBUTION_HEIGHT = 16
// Tall file embeds (3D models, projected with FileEmbed's own predicate so both sides agree)
// render a fixed
// RESERVED_DIMLESS_MEDIA_HEIGHT window instead of the compact chip row: `.modelViewer` in
// file-embed.module.scss, 400px + 10px top margin, inside the files/payload container's 15px top
// margin.
const BUCKETS_TALL_FILE_EMBED_CHROME = 25
const BUCKETS_EMBED_BASE_HEIGHT = 128
const BUCKETS_EMBED_STUB_HEIGHT = 156
// PostEmbed windowing: the calibrated base above includes the .embedContainer margin-top that
// sits OUTSIDE the clipped .embedComp box, so the window applies to base - margin + content.
const POST_EMBED_OUTER_MARGIN_HEIGHT = 15
// Render-side pin for the .embedComp box (post-embed.module.scss .embedWindow max-height +
// PostEmbed's reserved-minHeight clamp) — the estimator reserves min(inner estimate, window).
export const POST_EMBED_WINDOW_PX = 400
// .embedContainer margins 15+15 + .embedComp padding-right 5 narrow the embed's text column.
const POST_EMBED_HORIZONTAL_CHROME = 35
export const postEmbedAnswerWidth = estimatedWidth => {
    const width = Number(estimatedWidth)
    if (!Number.isFinite(width) || width <= 0) return 0

    return Math.max(0, Math.round(width) - POST_EMBED_HORIZONTAL_CHROME)
}
// ——— In-content embeds (rendered from links in delta_json, not answer.url/answer.embeds[]) ———
// Each family renders in a `styles.embedContainer` wrapper with a 15px top margin (inline or
// via the class), and under the flag Answer.jsx pins the wrapper to a fixed-height window
// (overflow hidden + fade) so rendered height ≡ the reserved constant even as the widget
// hydrates/grows. Constants are single-source here; Answer.jsx imports them for the pins.
const CONTENT_EMBED_TOP_MARGIN = 15
// TdpMessageEmbed (tdp-message-embed.module.scss + tdp-cards.module.scss): .container padding
// 8+15 + .embedFrame borders 2 + embed value card (padding 14×2 + header ~38+14 + value row
// ~56 + three sector rows 14+3×38+2×5 + split bar 10+8) + wide share row (46, report posts)
// + footer (margin 14 + border 1 + padding 10×2 + ~16 label) ≈ the measured rendered value
// card (~411px incl. footer; sentiment ~397, share rewards with five sharers ~400) plus a
// little slack the share row absorbs. Shareless contexts (composer previews) leave more.
export const TDP_EMBED_WINDOW_PX = 420
// LeaderboardMessageEmbed → UpvalueLeaderboardWidget/LeaderboardPanel: reserve room for
// all seven categories, the stacked heading, and both the share/join pill and footer.
// Includes container padding, card borders, row gaps, and a little line-height slack.
export const LEADERBOARD_EMBED_WINDOW_PX = 534
// StreamEmbed (stream-embed.module.scss): the static widget surface is stretched to this whole
// window; its banner remains pinned to 140 (see .bannerPinned) under a 72px logo, name, host
// line, a description clamped to two lines (~46) and the stats footer. At phone width that
// footer wraps to three rows (members + upvalue, host share, threads + activity): ~373.
export const STREAM_EMBED_WINDOW_PX = 380
// MarketCardEmbed (market-card-embed.module.scss): container stretches to fill the window
// (height 100%), so rendered height ≡ this constant by construction — the header, artwork
// (flexing), and receipt rows share it. Sized for ~215px of artwork under the header.
export const MARKET_EMBED_WINDOW_PX = 420
// Text-derived link previews (twitter-only in read mode) render TwitterUrl, whose tweet data
// loads CLIENT-SIDE (no url record on the answer) — height is unknowable up front, so the
// render pins the wrapper to the card's fixed-shell skeleton height and the estimator reserves
// the same constant. Loaded tweets clip into the window fade (or leave slack) instead of
// resizing the row.
export const TEXT_LINK_PREVIEW_WINDOW_PX = TWEET_SKELETON_HEIGHT_PX

// Mirrors the read-mode render gates in Answer.jsx (~4550-4700) exactly: stream embeds gate on
// extractStreamEmbedConfig (already applied during extraction), leaderboard/TDP embeds render
// per remaining link, and AT MOST ONE text-derived link preview renders — only when the answer
// has no url attachment and no attached image — after dropping entries that match the attached
// url address or extract as leaderboard/TDP configs (`.slice(0, 1)`). Suppressed preview urls
// and the oversized-delta guard are applied while building the answer geometry projection.
const estimateContentEmbedsHeight = projection => {
    const links = projection?.contentEmbeds
    let height = 0
    height += (links?.streamCount || 0) * (CONTENT_EMBED_TOP_MARGIN + STREAM_EMBED_WINDOW_PX)
    height +=
        (links?.leaderboardCount || 0) * (CONTENT_EMBED_TOP_MARGIN + LEADERBOARD_EMBED_WINDOW_PX)
    height += (links?.tdpCount || 0) * (CONTENT_EMBED_TOP_MARGIN + TDP_EMBED_WINDOW_PX)
    height += (links?.marketCount || 0) * (CONTENT_EMBED_TOP_MARGIN + MARKET_EMBED_WINDOW_PX)
    height +=
        (links?.renderedTextPreviewCount || 0) *
        (CONTENT_EMBED_TOP_MARGIN + TEXT_LINK_PREVIEW_WINDOW_PX)
    return height
}

// Long paragraphs wrap: newline counting alone sees one line where the renderer shows up to the
// native collapse clamp. Count visual lines at the row's current width and take the max with
// the explicit count. Counting the whole text instead systematically over-counts short
// multi-line posts by a line, and that bias sums across a freshly-mounted virtualizer window.
const resolveWrapCharsPerLine = estimatedWidth => {
    const width = Number(estimatedWidth)
    if (!Number.isFinite(width) || width <= 0) return BUCKETS_WRAP_CHARS_PER_LINE

    const textWidth = Math.max(0, width - BUCKETS_TEXT_HORIZONTAL_CHROME)
    return clamp(
        Math.floor(textWidth / BUCKETS_AVERAGE_CHAR_WIDTH_PX),
        BUCKETS_MIN_WRAP_CHARS_PER_LINE,
        BUCKETS_WRAP_CHARS_PER_LINE,
    )
}

const estimateWrappedTextLines = (
    projection,
    { estimatedWidth = 0, useFullContentMetadata = false } = {},
) => {
    const explicitLines = estimateTextLines(projection) || 1
    try {
        const text = projection?.text || ''
        const wrapCharsPerLine = resolveWrapCharsPerLine(estimatedWidth)
        const unbrokenTokenCharsPerLine = Math.max(
            20,
            Math.floor(
                wrapCharsPerLine *
                    (BUCKETS_UNBROKEN_TOKEN_CHARS_PER_LINE / BUCKETS_WRAP_CHARS_PER_LINE),
            ),
        )
        let wrappedLines = 0
        for (const paragraph of String(text).split('\n')) {
            const longestToken = Math.max(
                0,
                ...String(paragraph)
                    .split(/\s+/)
                    .map(token => token.length),
            )
            wrappedLines += Math.max(
                1,
                Math.ceil(paragraph.length / wrapCharsPerLine),
                Math.ceil(longestToken / unbrokenTokenCharsPerLine),
            )
        }
        const fullContentCharCount = Number(projection?.contentCharCount || 0)
        if (useFullContentMetadata && fullContentCharCount > String(text).length) {
            wrappedLines = Math.max(
                wrappedLines,
                Math.ceil(fullContentCharCount / wrapCharsPerLine),
            )
        }
        return Math.max(explicitLines, wrappedLines)
    } catch (error) {
        return explicitLines
    }
}

// Mobile width contract (lib/mediaBuckets): tile heights are clamped to what contain-fit media
// actually renders at on narrow cards, exactly like the payload renderers. Ratios ride the
// projection (width-independent, projections are retained across resizes); the width is
// resolved fresh per estimate. Without a ratio or a mobile width the bucket stays as-is.
const clampedPayloadTileHeight = (bucketHeight, ratio, tileWidth) => {
    const height = Number(bucketHeight) || 0
    const aspectRatio = Number(ratio)
    const width = Number(tileWidth)
    if (!height) return height
    if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) return height
    if (!Number.isFinite(width) || width <= 0) return height
    return Math.min(height, Math.max(1, Math.round(width * aspectRatio)))
}

// Answer selects attached images and AI-run outputs from one chronological list capped at four;
// PayloadContainer then groups those selected items into per-kind rows. Each tile buckets from
// its OWN dimensions. The answer-level recording remains a separate addend. Shared by the
// top-level payload term and the nested answer inside PostEmbed, which uses the same pipeline.
// postLens rows clamp tiles to the indented reply column; multi-item embed rows use their nested
// answer width. Renderers derive the same widths so estimate === rendered.
const estimateAnswerPayloadHeight = (
    projection,
    { postLens = false, isEmbed = false, estimatedWidth = 0, mediaLayoutKey = null } = {},
) => {
    const media = projection?.media || {}
    let payloadHeight = 0
    const mediaContentWidth = payloadMediaContentWidth({ postLens, cardWidth: estimatedWidth })
    const embedGridMediaContentWidth = isEmbed
        ? payloadMediaContentWidth({ embed: true, cardWidth: estimatedWidth })
        : null
    const imageTileRatios = media.imageTileRatios || []
    const aiTileRatios = media.aiTileRatios || []
    const legacyTiles = (media.imageTileHeights || []).map((height, index) => ({
        height,
        ratio: imageTileRatios[index] ?? null,
        kind: 'image',
        attributed: false,
    }))
    if (media.hasSingleImage) {
        payloadHeight +=
            clampedPayloadTileHeight(
                media.singleImageHeight || 0,
                media.singleImageRatio,
                mediaContentWidth,
            ) + BUCKETS_SINGLE_IMAGE_CHROME
    }
    legacyTiles.push(
        ...(media.aiTileHeights || []).map((height, index) => ({
            height,
            ratio: aiTileRatios[index] ?? null,
            kind: media.aiTileKinds?.[index] || 'image',
            attributed: true,
        })),
    )
    // New projections carry the exact combined chronological selection made by Answer. Fall
    // back to the legacy parallel arrays for retained fingerprints created before this field.
    const capped = Array.isArray(media.visualTiles)
        ? media.visualTiles.slice(0, 4)
        : legacyTiles.slice(0, 4)
    if (capped.length) {
        // PayloadContainer groups all visual kinds (image + video) into ONE MediaContainer
        // grid row, so an image+video payload takes the same 2-up layout and single-media
        // height budget a two-image payload gets — stacked per-kind rows claimed two media
        // units and the stream's payload cap sliced the second one mid-frame. Non-visual
        // kinds (audio/file/status) stay separate rows on the renderer; their heights are
        // charged by the dedicated audio/file terms below, so visual tiles are the only
        // grid input here.
        const rowKindForTile = tile => payloadMediaRowKind(tile.kind)
        const rows = [...new Set(capped.map(rowKindForTile))].map(kind =>
            capped.filter(tile => rowKindForTile(tile) === kind),
        )
        rows.forEach((row, rowIndex) => {
            const rowMediaContentWidth =
                isEmbed && row.length > 1 ? embedGridMediaContentWidth : mediaContentWidth
            // Attached multi-item rows default to the X-style carousel and generations to the
            // grid (lib/payloadMediaLayout, keyed by the answer id for the viewer's flip).
            // MediaContainer resolves the same layout from the same tiles and key. The strip
            // is one uniform band whose height comes from the content width alone — tile
            // count and aspect never change it — under the bare-grid chrome.
            const carouselRow =
                row.length > 1 &&
                resolvePayloadMediaLayout({
                    key: mediaLayoutKey,
                    tiles: row.map(tile => ({ generated: tile.attributed })),
                }) === 'carousel'
            if (carouselRow) {
                payloadHeight +=
                    payloadMediaCarouselHeight(rowMediaContentWidth) + BUCKETS_MEDIA_GRID_CHROME
                if (rowIndex > 0) payloadHeight += BUCKETS_MIXED_MEDIA_ROW_GAP
                return
            }
            const tileWidth =
                row.length === 1
                    ? rowMediaContentWidth
                    : payloadMediaGridTileWidth(rowMediaContentWidth)
            const rowHeights = row.map(tile =>
                clampedPayloadTileHeight(tile.height, tile.ratio, tileWidth),
            )
            // Reply-card grids use square tiles sized to their computed column width. Other
            // surfaces retain the single-media grid budget cap. MediaContainer uses the same
            // helper and width contract, so reserved height matches the rendered frames.
            payloadHeight +=
                payloadMediaGridLayout(rowHeights, BUCKETS_MEDIA_GRID_GAP, {
                    squareTileHeight: (postLens || isEmbed) && row.length > 1 ? tileWidth : null,
                })?.height ?? 0
            payloadHeight +=
                row.length > 1
                    ? BUCKETS_MEDIA_GRID_CHROME
                    : row.some(tile => tile.attributed)
                    ? BUCKETS_MEDIA_SECTION_CHROME
                    : BUCKETS_SINGLE_IMAGE_CHROME
            if (rowIndex > 0) payloadHeight += BUCKETS_MIXED_MEDIA_ROW_GAP
        })
    }
    if (answerHasRecording(projection)) {
        payloadHeight +=
            clampedPayloadTileHeight(
                media.recordingHeight || 0,
                media.recordingRatio,
                mediaContentWidth,
            ) + BUCKETS_MEDIA_SECTION_CHROME
    }
    // PayloadContainer combines generated and attached audio, groups it into the same two-up
    // grid, and applies the outer wrapper once. Mirror that shape instead of charging each file.
    const fileRows = projection?.fileRows || []
    const attachedAudioCount = fileRows.filter(file => file.audio).length
    const attachedOtherCount = fileRows.filter(file => !file.audio).length
    const aiAudioOutputCount = Number(media.aiAudioOutputCount || 0)
    const aiReadyAudioOutputCount = Number(
        media.aiReadyAudioOutputCount ?? media.aiAudioOutputCount ?? 0,
    )
    const audioOutputCount = Math.min(4, aiAudioOutputCount + attachedAudioCount)
    const audioReadyOutputCount = Math.min(4, aiReadyAudioOutputCount + attachedAudioCount)
    // A fused release docks its transport inside the artwork tile, so the audio claims no
    // vertical band at all. PayloadContainer decides this from the live items; both sides go
    // through lib/combinedMedia so the reservation matches what paints.
    const combinesMedia = projectionCombinesMedia({
        visualTiles: capped,
        audioOutputCount,
        audioReadyOutputCount,
        hasRecording: answerHasRecording(projection),
        hasSingleImage: !!media.hasSingleImage,
        otherOutputCount:
            attachedOtherCount +
            Number(media.aiOtherOutputCount || 0) +
            Number(media.aiStatusOutputCount || 0),
    })
    if (audioOutputCount > 0 && !combinesMedia) {
        const audioRows = Math.ceil(audioOutputCount / 2)
        payloadHeight +=
            audioRows * BUCKETS_AUDIO_PAYLOAD_SURFACE_OUTER_HEIGHT +
            Math.max(0, audioRows - 1) * BUCKETS_MEDIA_GRID_GAP +
            BUCKETS_AUDIO_PAYLOAD_CHROME +
            Math.min(audioRows, aiAudioOutputCount) * BUCKETS_AUDIO_ATTRIBUTION_HEIGHT
    }
    return payloadHeight
}

// Embedded answers render a FULL nested Answer inside the PostEmbed chrome — media tiles, text,
// async hydration growth — not just a text preview. Under the flag the render clips the
// .embedComp box at POST_EMBED_WINDOW_PX, so the reservation is the outer margin + min(inner
// content estimate, window); the window bounds whatever entity-cache hydration adds later.
const resolvePostEmbedProjectionLayout = (embedProjection, { estimatedWidth = 0 } = {}) => {
    if (!embedProjection) {
        const reservedHeight = BUCKETS_EMBED_STUB_HEIGHT - POST_EMBED_OUTER_MARGIN_HEIGHT
        return { naturalHeight: reservedHeight, reservedHeight, isClipped: false }
    }

    const embedWidth = postEmbedAnswerWidth(estimatedWidth)
    const projectedAnswer = { geometry_projection: embedProjection }
    const answerHeight = estimateInactiveAnswerHeight({
        answer: projectedAnswer,
        estimatedWidth: embedWidth,
        isEmbed: true,
        textIsCollapsed: true,
    })
    const innerHeight =
        BUCKETS_EMBED_BASE_HEIGHT -
        POST_EMBED_OUTER_MARGIN_HEIGHT +
        Math.max(0, answerHeight - STATIC_GEOMETRY_REPLY_BASE_HEIGHT_PX)
    return {
        naturalHeight: innerHeight,
        reservedHeight: Math.min(innerHeight, POST_EMBED_WINDOW_PX),
        isClipped: innerHeight > POST_EMBED_WINDOW_PX,
    }
}

export const resolvePostEmbedStaticLayout = ({ answer = null, estimatedWidth = 0 } = {}) => {
    const source = resolveHeightEstimationSource(answer)
    return resolvePostEmbedProjectionLayout(resolveEmbeddedAnswerGeometryProjection(source), {
        estimatedWidth,
    })
}

const estimateEmbeddedAnswerHeight = (embedProjection, { estimatedWidth = 0 } = {}) =>
    POST_EMBED_OUTER_MARGIN_HEIGHT +
    resolvePostEmbedProjectionLayout(embedProjection, { estimatedWidth }).reservedHeight

const estimateUrlSelectionHeight = ({ selection = '', estimatedWidth = 0 } = {}) => {
    if (!selection) return 0

    const normalizedSelection = String(selection).replace(/\s+/g, ' ').trim()
    if (!normalizedSelection) return 0

    const lines = clamp(
        Math.ceil(
            Math.min(normalizedSelection.length, 280) / resolveWrapCharsPerLine(estimatedWidth),
        ),
        1,
        BUCKETS_URL_SELECTION_MAX_LINES,
    )
    return lines * BUCKETS_URL_SELECTION_LINE_HEIGHT
}

const estimateGenericUrlAnswerHeight = ({ projection = null, estimatedWidth = 0 } = {}) => {
    const ordinalFrameChrome = extractOrdinalOutpointFromUrl(projection?.url?.address)
        ? BUCKETS_ORDINAL_URL_FRAME_CHROME
        : 0
    return (
        RESERVED_URL_CARD_HEIGHT +
        BUCKETS_URL_ANSWER_TOP_MARGIN +
        ordinalFrameChrome +
        estimateUrlSelectionHeight({
            selection: projection?.url?.visibleSelectionText || '',
            estimatedWidth,
        })
    )
}

export const estimateInactiveAnswerHeight = ({
    answer: answerInput,
    estimatedWidth = 0,
    isTitle = false,
    postLens = false,
    isEmbed = false,
    editMode = false,
    textIsCollapsed = false,
    maxPayloadHeight = null,
    maxReservedHeight = null,
    textLineLimit = null,
    useFullContentMetadata = false,
    tweetExpanded = false,
}) => {
    const buckets = mediaBucketsEnabled()
    // Evicted answers remount as bare `{id, _placeholder}` stubs; substitute the retained
    // height fingerprint so the estimate stays exact instead of collapsing to the fallback.
    const answer = buckets ? resolveHeightEstimationSource(answerInput) : answerInput
    const projection = resolveAnswerGeometryProjection(answer)
    const fallback = buckets
        ? isTitle
            ? STATIC_GEOMETRY_TITLE_BASE_HEIGHT_PX
            : STATIC_GEOMETRY_REPLY_BASE_HEIGHT_PX
        : isTitle
        ? STREAM_TITLE_FALLBACK_HEIGHT
        : STREAM_REPLY_FALLBACK_HEIGHT
    if (!answer || !projection) return fallback

    const replyMediaEstimatedWidth =
        mobileReplyMediaEstimatedWidth({ postLens, cardWidth: estimatedWidth }) ?? estimatedWidth

    // Under the flag, "collapsed" IS the native ~15-line clamp (+ reserved "more" slot), so the
    // same line ceiling applies whether or not the caller marks the row collapsed — routing
    // collapsed rows to a tighter clamp would under-reserve them by hundreds of px.
    const configuredTextLineLimit = Number(textLineLimit)
    const textLineLimitValue =
        (Number.isFinite(configuredTextLineLimit) || configuredTextLineLimit === Infinity) &&
        configuredTextLineLimit > 0
            ? configuredTextLineLimit
            : null
    const maxLines = textLineLimitValue
        ? textLineLimitValue
        : buckets
        ? BUCKETS_MAX_TEXT_LINES
        : textIsCollapsed
        ? isTitle
            ? 4
            : 5
        : isTitle
        ? 5
        : 7
    const estimatedTextLines =
        (buckets
            ? estimateWrappedTextLines(projection, { estimatedWidth, useFullContentMetadata })
            : estimateTextLines(projection)) || 1
    const expandedTextEstimate =
        buckets && textLineLimitValue === Infinity && estimatedTextLines > BUCKETS_MAX_TEXT_LINES
    const lineCount = clamp(
        expandedTextEstimate
            ? Math.ceil(estimatedTextLines * BUCKETS_EXPANDED_TEXT_WRAP_SAFETY_FACTOR)
            : estimatedTextLines,
        1,
        maxLines,
    )
    let height = fallback + (lineCount - 1) * STREAM_TEXT_LINE_HEIGHT

    // Reply-card PostAnswer keeps its 40px avatar in an absolute column. With no rendered body
    // row, the byline header grows from 24px to 44px to contain that avatar; mirror the exact
    // renderer gate here. Editing always renders a body row, even when the saved post is empty.
    const replyCardBodylessHeaderExtra =
        postLens && !editMode && projection.hasPostAnswerContent === false
            ? REPLY_CARD_BODYLESS_HEADER_EXTRA_HEIGHT_PX
            : 0
    height += replyCardBodylessHeaderExtra

    let tweetCardHeight = 0
    if (buckets) {
        const payloadHeight = estimateAnswerPayloadHeight(projection, {
            postLens,
            isEmbed,
            estimatedWidth,
            // Retained fingerprints keep the id, so an evicted row still reads its override.
            mediaLayoutKey: answer?.id ?? null,
        })
        const payloadCap = Number(maxPayloadHeight)
        height +=
            Number.isFinite(payloadCap) && payloadCap > 0 && !answerHasAudioPayload(projection)
                ? Math.min(payloadHeight, payloadCap)
                : payloadHeight
        // The AI-input strip renders OUTSIDE the capped payload container (its own sibling
        // row above it), so its fixed height adds after the cap.
        if (Number(projection.media?.aiInputMediaCount || 0) > 0) {
            height += BUCKETS_AI_INPUT_STRIP_HEIGHT
        }
    } else if (answerHasMedia(projection) || projection.media.aiVisualOutputCount) {
        // Unflagged geometry frames render these estimates as visible minHeight, so reserve one
        // generic media block for the unified payload grid without double-counting mixed media.
        height += STREAM_MEDIA_PLACEHOLDER_HEIGHT
    }
    if (buckets) {
        // An answer with visual media renders its url as a plain link, not a preview card.
        if (projection.url?.present && !answerHasMedia(projection)) {
            if (projection.url.kind === 'tweet') {
                // Tweets render via our local TwitterUrl card, whose height tracks text length
                // (wrapped at the row's width, full text when the "more" toggle fired) + media
                // dims + any quote/website embed window rather than a constant.
                tweetCardHeight = estimateTweetCardProjectionHeight(projection.url.tweet, {
                    width: replyMediaEstimatedWidth,
                    expanded: tweetExpanded,
                })
                height += tweetCardHeight
            } else if (projection.url.kind === 'youtube') {
                // YouTube-style embeds render a fixed player frame inside the normal payload
                // container chrome (top margin, border, and answer spacing).
                height += RESERVED_YOUTUBE_EMBED_HEIGHT + BUCKETS_YOUTUBE_CHROME
            } else {
                // Generic link cards are pinned to the constant, then UrlAnswer adds its top
                // margin and an optional visible quoted selection under the preview.
                height += estimateGenericUrlAnswerHeight({
                    projection,
                    estimatedWidth: replyMediaEstimatedWidth,
                })
            }
        }
        if (hasArrayItems(projection.embeds)) {
            projection.embeds.forEach(embed => {
                // Embeds without their own content (privacy stubs, bare references) render the
                // compact PostEmbed placeholder, not a link card.
                height += embed.looksLikeAnswer
                    ? estimateEmbeddedAnswerHeight(embed.projection, {
                          estimatedWidth: replyMediaEstimatedWidth,
                      })
                    : BUCKETS_EMBED_STUB_HEIGHT
            })
        }
        // In-content embeds rendered from links in the post text (stream/leaderboard/TDP
        // widgets + the single text-derived tweet preview) — each pinned to its fixed window.
        height += estimateContentEmbedsHeight(projection)
    } else if (answerHasPreview(projection)) {
        height += STREAM_URL_PLACEHOLDER_HEIGHT
    }
    if (hasArrayItems(projection.fileRows)) {
        if (buckets) {
            // Per-entry: tall embeds (3D model viewer) render the
            // fixed 400px window + margins; everything else renders the compact chip row.
            projection.fileRows.forEach(file => {
                if (file.audio) return
                height += file.tall
                    ? RESERVED_DIMLESS_MEDIA_HEIGHT + BUCKETS_TALL_FILE_EMBED_CHROME
                    : BUCKETS_FILE_ROW_HEIGHT
            })
        } else {
            height += projection.fileRows.length * STREAM_FILE_ROW_HEIGHT
        }
    }
    const receiptHeights =
        !editMode && Array.isArray(answer.payment_receipts)
            ? answer.payment_receipts.map(receipt => {
                  const rows = Math.max(1, receipt.outputs?.length || 0)
                  return 170 + (rows - 1) * 50 + (rows > 1 ? 43 : 0)
              })
            : []
    const paymentReceiptHeight = receiptHeights.reduce((total, value) => total + value, 0)
    height += paymentReceiptHeight
    if (projection.pollOptionCount) {
        height += Math.min(4, projection.pollOptionCount) * 28
    }

    // Buckets can stack a 500px portrait frame plus a recursively-estimated embed, so the
    // flagged ceiling must clear the tallest legitimate reservation — a clipped estimate is a
    // reintroduced estimate-vs-render mismatch. Terms that are bounded by their own contract
    // (receipts, the body-less header, and the tweet card: 20/120-line text caps, bucketed
    // media, the projected quote window) ride above the ceiling like the receipts already do.
    const configuredMaxReservedHeight = Number(maxReservedHeight)
    const ceiling =
        (Number.isFinite(configuredMaxReservedHeight) ||
            configuredMaxReservedHeight === Infinity) &&
        configuredMaxReservedHeight > 0
            ? configuredMaxReservedHeight
            : buckets
            ? isTitle
                ? 1500
                : 1400
            : isTitle
            ? 520
            : 420
    return clamp(
        height,
        fallback,
        ceiling + replyCardBodylessHeaderExtra + paymentReceiptHeight + tweetCardHeight,
    )
}

export const estimateInactiveReservedHeight = ({
    answer,
    estimatedWidth = 0,
    isThin = false,
    isTitle = false,
    postLens = false,
    editMode = false,
    textIsCollapsed = false,
    maxPayloadHeight = null,
    maxReservedHeight = null,
    textLineLimit = null,
    useFullContentMetadata = false,
    tweetExpanded = false,
}) =>
    isThin
        ? STREAM_THIN_ANSWER_HEIGHT
        : estimateInactiveAnswerHeight({
              answer,
              estimatedWidth,
              isTitle,
              postLens,
              editMode,
              textIsCollapsed,
              maxPayloadHeight,
              maxReservedHeight,
              textLineLimit,
              useFullContentMetadata,
              tweetExpanded,
          })

export const resolveMeasuredInactiveReservedHeight = ({ height = 0, isThin = false } = {}) => {
    const measuredHeight = Math.ceil(Number(height || 0))
    if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) return null

    return isThin ? STREAM_THIN_ANSWER_HEIGHT : measuredHeight
}
